import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api, fileUrl } from "@/lib/api";
import SpecialtyWheel from "@/components/SpecialtyWheel";
import BottomSheet from "@/components/BottomSheet";

function PillSelect({ label, value, options, onChange, placeholder, testid }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  return (
    <div className="relative flex-1" ref={ref}>
      <button
        data-testid={testid}
        onClick={() => setOpen((o) => !o)}
        style={{ backgroundColor: "rgba(255,255,255,0.06)", height: 56 }}
        className="flex w-full items-center justify-between rounded-3xl px-5 text-left ring-1 ring-white/20"
      >
        <span className="text-xs font-medium text-white/60">{label}</span>
        <span className="ml-2 truncate text-white/80" style={{ fontSize: 15, fontWeight: 400 }}>{value || placeholder}</span>
      </button>
      {open && (
        <div className="no-scrollbar absolute left-0 right-0 z-50 mt-2 max-h-52 overflow-y-auto rounded-2xl border border-white/15 bg-neutral-800/45 p-1 shadow-2xl" style={{ backdropFilter: "blur(40px)", WebkitBackdropFilter: "blur(40px)" }}>
          {options.length === 0 && (
            <div className="px-4 py-3 text-sm text-white/50">Sin opciones</div>
          )}
          <button
            data-testid={`${testid}-option-clear`}
            onClick={() => {
              onChange("");
              setOpen(false);
            }}
            className={`block w-full rounded-xl px-4 py-2.5 text-left text-base ${
              !value ? "bg-white/15 text-white" : "text-white/80 hover:bg-white/10"
            }`}
          >
            {placeholder}
          </button>
          {options.map((o) => (
            <button
              key={o}
              data-testid={`${testid}-option-${o}`}
              onClick={() => {
                onChange(o);
                setOpen(false);
              }}
              className={`block w-full rounded-xl px-4 py-2.5 text-left text-base ${
                o === value ? "bg-white/15 text-white" : "text-white/80 hover:bg-white/10"
              }`}
            >
              {o}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function BannerCarousel({ banners }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (banners.length <= 1) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % banners.length), 4000);
    return () => clearInterval(t);
  }, [banners.length]);
  if (banners.length === 0) return null;
  return (
    <div className="relative h-[86px] overflow-hidden rounded-2xl" data-testid="search-banner">
      <div
        className="flex h-full transition-transform duration-700 ease-out"
        style={{ transform: `translateX(-${idx * 100}%)` }}
      >
        {banners.map((b) => (
          <a
            key={b.id}
            href={b.link || "#"}
            target="_blank"
            rel="noreferrer"
            className="relative block h-full w-full shrink-0"
          >
            <img src={fileUrl(b.image)} alt="" className="h-full w-full object-cover" />
          </a>
        ))}
      </div>
    </div>
  );
}

export default function SearchSheet({ open, onOpenChange, country, onToggleCountry }) {
  const navigate = useNavigate();
  const [kind, setKind] = useState("doctor");
  const [locations, setLocations] = useState([]);
  const [insurances, setInsurances] = useState([]);
  const [banners, setBanners] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [province, setProvince] = useState("");
  const [city, setCity] = useState("");
  const [zone, setZone] = useState("");

  useEffect(() => {
    api.get("/specialties").then((r) => setSpecialties(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!open) return;
    setProvince("");
    setCity("");
    setZone("");
    api.get(`/locations?country=${country}`).then((r) => setLocations(r.data)).catch(() => {});
    api.get(`/banners?country=${country}`).then((r) => setBanners(r.data)).catch(() => {});
    api.get(`/specialties?country=${country}`).then((r) => setSpecialties(r.data)).catch(() => {});
  }, [country, open]);

  const provinceOptions = useMemo(() => locations.map((l) => l.province), [locations]);
  const cityOptions = useMemo(
    () => locations.find((l) => l.province === province)?.cities || [],
    [locations, province]
  );
  const zoneOptions = useMemo(() => {
    const loc = locations.find((l) => l.province === province);
    return (loc?.city_zones || {})[city] || [];
  }, [locations, province, city]);

  const go = (specialty) => {
    const params = new URLSearchParams({ kind, country });
    if (province) params.set("province", province);
    if (city) params.set("city", city);
    if (zone) params.set("zone", zone);
    if (specialty) params.set("specialty", specialty);
    onOpenChange(false);
    navigate(`/resultados?${params.toString()}`);
  };

  return (
    <BottomSheet open={open} onOpenChange={onOpenChange} className="glass-dark h-[700px] rounded-t-[34px] px-5 pb-4">
      <div className="flex min-h-0 flex-1 flex-col pt-3" data-testid="search-sheet">
        {/* Kind toggle */}
        <div className="relative z-20 mx-auto mt-3 flex items-center gap-2 rounded-full p-1.5" style={{ width: 299, height: 51, backgroundColor: "#595959" }}>
          {[
            ["doctor", "Odontólogos"],
            ["clinic", "Clínicas"],
          ].map(([k, label]) => (
            <button
              key={k}
              data-testid={`kind-toggle-${k}`}
              onClick={() => setKind(k)}
              className={`relative flex h-full flex-1 items-center justify-center rounded-full text-xl font-normal transition-colors ${
                kind === k ? "text-white" : "text-white/70"
              }`}
            >
              {kind === k && (
                <motion.div
                  layoutId="kind-pill"
                  className="absolute inset-0 rounded-full bg-neutral-900"
                  transition={{ type: "spring", stiffness: 480, damping: 34 }}
                />
              )}
              <span className="relative z-10">{label}</span>
            </button>
          ))}
        </div>

        {/* Province / City */}
        <div className="relative z-20 mt-5 flex gap-3">
          <PillSelect
            testid="province-select"
            label="Provincia"
            value={province}
            placeholder="Todas"
            options={provinceOptions}
            onChange={(v) => {
              setProvince(v);
              setCity("");
            }}
          />
          <PillSelect
            testid="city-select"
            label="Ciudad"
            value={city}
            placeholder="Todas"
            options={cityOptions}
            onChange={(v) => {
              setCity(v);
              setZone("");
            }}
          />
        </div>

        {/* Zone (only when the selected city has zones) */}
        {zoneOptions.length > 0 && (
          <div className="relative z-10 mt-3">
            <PillSelect
              testid="zone-select"
              label="Zona"
              value={zone}
              placeholder="Todas las zonas"
              options={zoneOptions}
              onChange={setZone}
            />
          </div>
        )}

        {/* Specialty wheel */}
        <div className="relative z-0 mt-3 min-h-0 flex-1 overflow-hidden">
          <SpecialtyWheel items={specialties} onSelect={go} />
        </div>

        {/* Banner */}
        <div className="safe-bottom mt-2">
          <BannerCarousel banners={banners} />
        </div>
      </div>
    </BottomSheet>
  );
}
